import { useContact } from "@/OneLiners";
import { BirthWishModel } from "../BirthWishModel";
import { ActionResponse, ActionStatusHelper, dateCompare, RecordMap, useAuditEnricher } from "@/Shared";
import { ContactModel } from "@/Domains";


/**
 * =====================================================
 * Hook: useBirthWishEnricher
 * =====================================================
 *
 * Description:
 * Custom hook to enrich BirthWish domain data with all relevant relations.
 * Combines users, tasks, media files, and audit info into the BirthWishModel.
 *
 * Responsibilities:
 * - Fetch and maintain related domain data (users, tasks, media files)
 * - Perform audit enrichment in O(1) using precomputed user Map
 * - Track combined async status and errors
 * - Provide helper functions to enrich a single birthWish, by ID, or a full map
 *
 * Behavior:
 * - Resolves agent users efficiently
 * - Attaches audit fields (createdBy, updatedBy, etc.) using the shared audit hook
 * - Returns enriched data without unnecessary re-computation
 *
 * Notes:
 * - Compatible with both list and detail views of BirthWishes
 * - Designed for performance at scale with large agent/target datasets
 *
 * Author: Samano CASTRE
 * Date: July 17, 2025
 */
export function useBirthWishEnricher() {

    const {
        dataMap: contactMap,
        stateResponse: contactStateResponse
    } = useContact();

    /**
     * Centralized audit enrichment hook
     * - Provides O(1) user resolution via precomputed Map
     * - Exposes audit enrichment helpers and status/error
     */
    const {
        enricherResponse: auditEnricherResponse,
        userMap,
        getUserById,
        getEnrichedAudit,
    } = useAuditEnricher<BirthWishModel>();


    const enricherResponse: ActionResponse = {
        status: ActionStatusHelper.combine(
            auditEnricherResponse.status,
            contactStateResponse.status
        ),
        error: auditEnricherResponse.error ??
            contactStateResponse.error
    };

    /** =====================================================
             * Public Helper Functions
             * =====================================================
             *
             * These functions enrich BirthWish entities with:
             * - initiator user
             * - assigned agent
             * - related task
             * - enriched media files (small & large)
             * - audit info (createdBy, updatedBy, deletedBy, restoredBy)
             */

    /** =====================================================
     * Enrich an entire map of activities
     * 
     * @param birthWishMap - Map keyed by birthWish ID containing BirthWishModel or undefined
     * @returns A new Map with the same keys, each BirthWishModel enriched with:
     *          - initiatorUser
     *          - assignedAgent
     *          - task
     *          - enriched birthWishMediaFiles
     *          - audit info (createdByUser, updatedByUser, etc.)
     * 
     * Notes:
     * - Preserves keys even if the value is undefined
     * - Uses O(1) lookups via Maps for users, tasks, media files
     * ===================================================== */
    const getEnrichedBirthWishMap = (birthWishMap: RecordMap<BirthWishModel>): RecordMap<BirthWishModel> => {
        return birthWishMap.map<BirthWishModel>(getStrictEnrichedBirthWish);
    }

    /*
    const getEnrichedBirthWishById = (
        birthWishList : BirthWishModel[],
        birthWishId?: number | null): BirthWishModel | undefined => {

        const birthWish = birthWishList.find(birthWish => birthWish.id === Number(birthWishId));
        return birthWish ? getEnrichedBirthWish(birthWish) : undefined;
    }*/


    /** =====================================================
            * Enrich a single birthWish
            * 
            * @param birthWish - BirthWishModel or undefined
            * @returns The BirthWishModel enriched with:
            *          - initiatorUser
            *          - assignedAgent
            *          - task
            *          - enriched birthWishMediaFiles
            *          - audit info (createdByUser, updatedByUser, etc.)
            *          Returns undefined if input birthWish is undefined
            * 
            * Notes:
            * - Lookup of users, tasks, and media files is O(1)
            * - Handles missing media or task references gracefully
            * ===================================================== */
    const getEnrichedBirthWish = (birthWish?: BirthWishModel): BirthWishModel | undefined => {
        return birthWish ? getStrictEnrichedBirthWish(birthWish) : undefined;
    }

    const getStrictEnrichedBirthWish = (birthWish: BirthWishModel): BirthWishModel => {

        return {
            ...getEnrichedAudit(birthWish) as BirthWishModel,
            contact: contactMap.get(birthWish.contactId)
        }
    }

    /**
     * Returns a list of contacts whose birthdays occur in the current month
     * and who have not yet received a birthday wish for the current year.
     * 
     * Methodology:
     * - Uses a precomputed Map (birthWishIndex) for O(1) lookup of already sent wishes.
     * - Iterates over all contacts and filters those with valid birth dates in the current month.
     * - Excludes contacts who already have a birthday wish entry for the current year.
     * - Sorts the resulting contacts in ascending order by their birth day within the month.
     * 
     * @param birthWishMap Map of existing birthday wishes keyed by an internal ID.
     * @returns Array of ContactModel objects eligible for a birthday wish this month.
     */
    function getCurrentMonthBirthdays(birthWishMap: RecordMap<BirthWishModel>): RecordMap<ContactModel> {
        const now = new Date();
        const currentMonth = now.getMonth(); // 0 = Janvier, 1 = Février, etc.
        const currentYear = now.getFullYear();//

        const birthWishIndex = new Map(
            birthWishMap.toArray().map(bw => [`${bw.contact}-${bw.mailingYear}`, bw])
        );

        return contactMap.filter(contact => {
                if (!contact.person?.birthDate) return false;

                const birthDate = new Date(contact.person?.birthDate!);
                if (isNaN(birthDate.getTime())) return false; // date invalide

                if (birthDate.getMonth() === currentMonth) {
                    return !birthWishIndex.get(`${contact.id}-${currentYear}`);
                }
                return false;
            })
            .sort((a, b) => dateCompare(a.person.birthDate, b.person.birthDate));
    }

    return {
        userMap,
        contactMap,
        enricherResponse,
        getEnrichedBirthWish,
        getStrictEnrichedBirthWish,
        getEnrichedBirthWishMap,
        getCurrentMonthBirthdays,
    }
}